import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { quotationsAPI } from '@/lib/api-client'
import { projectsAPI } from '@/lib/api-client'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2 } from 'lucide-react'

const formSchema = z.object({
  title: z.string().min(1, 'Título é obrigatório'),
  projectId: z.string().optional(),
  notes: z.string().optional(),
})

type FormValues = z.infer<typeof formSchema>

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  quotation?: any
  onCreated?: (qc: any) => void
}

export function QuotationFormDialog({ open, onOpenChange, quotation, onCreated }: Props) {
  const queryClient = useQueryClient()
  const isEdit = !!quotation

  const { data: projectsData } = useQuery({
    queryKey: ['projects-list'],
    queryFn: () => projectsAPI.list({ limit: 100 }),
    enabled: open,
  })
  const projects = projectsData?.data || []

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { title: '', projectId: '', notes: '' },
  })

  useEffect(() => {
    if (quotation) {
      form.reset({
        title: quotation.title,
        projectId: quotation.projectId ?? '',
        notes: quotation.notes ?? '',
      })
    } else {
      form.reset({ title: '', projectId: '', notes: '' })
    }
  }, [quotation, open])

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const payload = {
        title: values.title,
        projectId: values.projectId || undefined,
        notes: values.notes || undefined,
      }
      return isEdit
        ? quotationsAPI.update(quotation.id, payload)
        : quotationsAPI.create(payload)
    },
    onSuccess: (result) => {
      toast.success(isEdit ? 'QC atualizado!' : 'Quadro de Concorrência criado!')
      queryClient.invalidateQueries({ queryKey: ['quotations'] })
      if (isEdit) queryClient.invalidateQueries({ queryKey: ['quotation', quotation.id] })
      onOpenChange(false)
      if (!isEdit && onCreated) onCreated(result)
    },
    onError: (error: Error) => toast.error(error.message),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar Quadro de Concorrência' : 'Novo Quadro de Concorrência'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Atualize as informações do QC.' : 'Preencha as informações para criar um novo QC.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          <div>
            <Label htmlFor="title">Título *</Label>
            <Input id="title" {...form.register('title')} placeholder="Ex: Piso vinílico — Bloco A" />
            {form.formState.errors.title && (
              <p className="text-sm text-destructive mt-1">{form.formState.errors.title.message}</p>
            )}
          </div>

          <div>
            <Label>Projeto</Label>
            <Select
              value={form.watch('projectId') || 'NONE'}
              onValueChange={(v) => form.setValue('projectId', v === 'NONE' ? '' : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecionar projeto (opcional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NONE">Sem projeto</SelectItem>
                {projects.map((p: any) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="notes">Observações</Label>
            <Input id="notes" {...form.register('notes')} placeholder="Observações gerais..." />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? 'Salvar' : 'Criar QC'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
